import {
  useEffect,
  useRef,
  forwardRef,
  DetailedHTMLProps,
  ImgHTMLAttributes,
  ForwardRefRenderFunction
} from 'react'
import LazyLoad from '@/lib/lazyLoad'
import useCombinedRefs from '@/hooks/useCombinedRefs'

declare global {
  interface Document {
    lazyLoad: LazyLoad;
  }
}

if (typeof document !== 'undefined' && !document.lazyLoad) {
  document.lazyLoad = new LazyLoad()
}

export type LazyImgProps = DetailedHTMLProps<
  ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  lazy?: number;
  defaultSrc?: string;
};

type LazyImageComponent<P> = ForwardRefRenderFunction<any, P>;

const LazyImg: LazyImageComponent<LazyImgProps> = (
  { lazy, src, alt, defaultSrc, ...props }: LazyImgProps,
  ref
) => {
  const innerRef = useRef(null)
  const combinedRef = useCombinedRefs(ref, innerRef)

  useEffect(() => {
    const element = innerRef.current
    document.lazyLoad.register(element, { lazy, imageSrc: src, defaultSrc })
    return () => document.lazyLoad.unregister(element)
  }, [lazy, src, defaultSrc])

  return <img alt={alt} data-src={src} {...props} ref={combinedRef} />
}

export default forwardRef(LazyImg)
